import { Injectable } from '@angular/core';
import { PurchaseStore } from './purchase.store';
import { tap, map, mapTo, switchMap } from 'rxjs/operators';
import { MemberPurchase } from './models/member-purchase.model';
import { Observable, of, timer, Subscription } from 'rxjs';
import { ApiService } from '../../shared/services/core/api.service';
import { PurchaseQuery } from './purchase.query';

import { MpThemeService } from '../../loyalty/marketplace/mp-theme.service';
import { LoggingService } from '../../shared/services/core/logging.service';
import { PurchaseOrderItemModel } from './models/purchase-order-item.model';
import { PurchaseStatus } from './models/purchase-status';
import { Router } from '@angular/router';
import { PurchaseBasketCollectionModel } from './models/purchase-models';
import { ProviderUiActionResponse } from '../../ecash/models/provider-ui-action-response.model';
import { config } from '../../shared/config';
import { MarketplaceVirtualEvent } from '../../loyalty/marketplace/models/marketplace-virtualevent.model';
import { Address } from '../../account/address/models/address.model';

@Injectable({ providedIn: 'root' })
export class PurchaseService {
  private timerSubscription: Subscription;

  constructor(
    private purchaseStore: PurchaseStore,
    private purchaseQuery: PurchaseQuery,
    private api: ApiService,
    private themeService: MpThemeService,
    private logger: LoggingService,
    private router: Router
  ) {}

  /** Fetch all member purchases and put them in the store. */
  fetchMemberPurchases(): Observable<null> {
    const request$ = this.api.get<MemberPurchase>('purchases').pipe(
      map((purchases) =>
        purchases.map((purchase) => ({
          ...purchase,
          theme: this.themeService.getTheme(purchase.ProcessProviderId),
        }))
      ),
      tap((purchases) => this.purchaseStore.set(purchases)),
      mapTo(null)
    );

    return this.purchaseQuery.getHasCache() ? of(null) : request$;
  }

  /** @deprecated, use checkOrderSuccessStatus: Fetch a purchase basket by order ID. */
  fetchPurchaseBasketByOrderId(orderId: string): Observable<PurchaseBasketCollectionModel> {
    return this.api.getById<PurchaseBasketCollectionModel>('purchases/order/basket', orderId);
  }

  /** update the address of a given purchase using its orderId and provided address model */
  updatePurchaseAddress(orderId: string, addressToUpdate: Address): Observable<null> {
    return this.api.post<Address>(`purchases/updateaddress/${orderId}`, addressToUpdate);
  }

  /** Fetch a member purchases from by ID and put it in the store. */
  fetchMemberPurchaseById(logId: number, portalId: string = ''): Observable<null> {
    return this.api.getAnySingle<MemberPurchase>(`purchases/${logId}?portalId=${portalId}`).pipe(
      map((purchase) => ({
        ...purchase,
        theme: this.themeService.getTheme(purchase.ProcessProviderId),
      })),
      tap((purchase) => {
        this.purchaseStore.upsert(logId, purchase);
      }),
      mapTo(null)
    );
  }

  async getDetailPurchase(logId: string) {
    return this.api.get(`purchases/${logId}`).toPromise();
  }

  getVirtualEventPurchaseDetails(logId: number): Observable<MarketplaceVirtualEvent> {
    return this.api.getAnySingle<MarketplaceVirtualEvent>(`marketplace/purchase/virtualEvent/${logId}`);
  }

  checkOrderSuccessStatus(orderId: string, successUrl: string, failureUrl: string, isRegistration = false) {
    this.timerSubscription = timer(0, 3000)
      .pipe(
        switchMap((time) => {
          if (time > 100) {
            this.timerSubscription.unsubscribe();
          }
          return this.fetchOrderById(orderId, isRegistration).pipe(
            map((x) => {
              if (x.Status === PurchaseStatus.Completed) {
                this.logger.info('Order succesful');
                this.timerSubscription.unsubscribe();
                this.router.navigateByUrl(successUrl + `?orderId=${orderId}`);
              } else if (x.Status === PurchaseStatus.Created) {
                this.logger.info('Order not completed wait more');
              } else {
                this.logger.info('Order failed');
                this.timerSubscription.unsubscribe();
                this.router.navigateByUrl(failureUrl + `?orderId=${orderId}`);
              }
            })
          );
        })
      )
      .subscribe();
  }

  completeTransaction(isRegistration = false): Observable<ProviderUiActionResponse> {
    let url = isRegistration ? 'register/processTransaction' : 'purchases/processTransaction';
    return this.api.post<string>(url, JSON.stringify(config.portalUrl + this.router.url));
  }

  fetchOrderById(orderId: string, isRegistration = false): Observable<PurchaseOrderItemModel> {
    let url = isRegistration ? 'register/order' : 'purchases/order';
    return this.api.getById<PurchaseOrderItemModel>(url, orderId);
  }

  /** Version 2 */

  fetchMemberPurchasesV2(): Observable<MemberPurchase[]> {
    return this.api.get<MemberPurchase>('purchases');
  }

  /** update the address of a given purchase using its orderId and provided address model */
  continueOrder(orderId: string): Observable<any> {
    return this.api.post<string>(`purchases/continue/${orderId}`, orderId);
  }

  getPurchaseByLogId(id: number) {
    return this.api.get(`purchases/${id}`);
  }
}
